import { Message } from '@bufbuild/protobuf'
import pluralize from 'pluralize'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react'

import { useUploadArtifact } from '@/api/document.hook'
import { DocumentSource } from '@/gen/artifacts/v1/model_pb'

import {
  DEFAULT_UPLOAD_CONSTRAINTS,
  SUPPORTED_FILE_TYPES_HINT,
  isSupportedFileType,
} from '@/const/artifact-upload-constraints'
import { artifactSubcategoryLabel } from '@/const/label'

import { useTrackCallback } from '@/lib/analytics/events'
import { isFilePasswordProtected } from '@/lib/file/file-utils'
import { rcFileToUint8Array } from '@/lib/file/rc-file-to-unit-8-array'

import { DocumentMetadata, UploadFilesRef } from '@/pages/settings-page/artifacts-section/types'

import { useToast } from '@/components/lemmala-design-system/toast/use-toast'
import { Upload } from '@/components/lemmala-design-system/upload/upload'

import { FileWithStatus, UploadFileWithStatus, UploadMutationParams } from './types'
import {
  UPLOAD_STILL_PROCESSING_MESSAGE,
  isTimeoutClassUploadError,
  toUploadErrorMessage,
} from './upload-error-message'

type UploadFilesProps = {
  hideTitle?: boolean
  metadata?: DocumentMetadata
  setIsUploadEnabled?: (isUploadEnabled: boolean) => void
  allowedFileTypes?: string[]
  setIsUploadingForParent?: (isUploading: boolean) => void
  uploadMutation?: (params: UploadMutationParams) => Promise<Message<string>>
  // If true, files will be uploaded immediately after the user adds them (no need to click an Upload button).
  immediateUpload?: boolean
}

export const UploadFiles = forwardRef<UploadFilesRef, UploadFilesProps>(
  (
    {
      metadata: { company, categoryMetadata } = {},
      setIsUploadEnabled,
      hideTitle,
      allowedFileTypes = DEFAULT_UPLOAD_CONSTRAINTS.supportedFileTypes,
      setIsUploadingForParent,
      uploadMutation,
      immediateUpload = false,
    },
    ref,
  ) => {
    const [fileList, setFileList] = useState<FileWithStatus[]>([])
    const [filePasswords, setFilePasswords] = useState<Record<string, string>>({})
    const { mutateAsync: defaultMutateAsync } = useUploadArtifact()
    const { toast } = useToast()
    const trackUpload = useTrackCallback('artifact.upload')
    const [isUploading, setIsUploading] = useState(false)
    const [pendingFiles, setPendingFiles] = useState<FileWithStatus[]>([])

    // Use provided uploadMutation or fall back to default
    const mutateAsync = uploadMutation || defaultMutateAsync

    useEffect(
      () => setIsUploadEnabled?.(isUploading || fileList.length > 0),
      [fileList, setIsUploadEnabled, isUploading],
    )

    const performUpload = useCallback(
      async (filesToUpload: FileWithStatus[], uploadCompanyId?: string) => {
        const acceptedFiles = filesToUpload.filter((f) => isSupportedFileType(f, allowedFileTypes))
        if (acceptedFiles.length === 0) return

        const companyId = uploadCompanyId || (company && company.id)

        let source = DocumentSource.MANUAL_UPLOAD
        if (companyId) {
          if (categoryMetadata) {
            source = DocumentSource.MANUAL_UPLOAD_WITH_COMPANY_AND_CATEGORY
          } else {
            source = DocumentSource.MANUAL_UPLOAD_WITH_COMPANY
          }
        }

        setIsUploading(true)
        setIsUploadingForParent?.(true)
        const uploadResults = await Promise.allSettled(
          acceptedFiles.map(async (rawFile) => {
            const { name: fileName, isEncrypted, uid } = rawFile
            const fileContent = await rcFileToUint8Array(rawFile)
            return mutateAsync({
              fileContent,
              fileName,
              source,
              companyId,
              categoryMetadata: categoryMetadata,
              isEncrypted,
              password: filePasswords[uid],
            })
          }),
        )

        const successfulUploads = uploadResults.filter((result) => result.status === 'fulfilled')
        const failedUploads = uploadResults.filter((result) => result.status === 'rejected')

        if (successfulUploads.length > 0) {
          const successFileWordPlural = pluralize('File', successfulUploads.length)
          const successIsAre = pluralize('is', successfulUploads.length)
          toast({
            variant: 'success',
            title: `${successfulUploads.length} files uploaded successfully`,
            description: `The ${successfulUploads.length} ${successFileWordPlural} ${successIsAre} being analyzed. You will be notified when analysis is finished.`,
          })
          trackUpload({
            company: company?.name,
            companyId: company?.id,
            fileCount: successfulUploads.length,
            source,
            files: acceptedFiles
              .filter((_, index) => uploadResults[index].status === 'fulfilled')
              .map((f) => f.name),
          })
        }

        const rejectedFiles = acceptedFiles
          .map((file, index) => {
            const stat = uploadResults[index]
            // Timed-out files plausibly succeeded server-side, so we treat them like successes
            // here: a sticky red row would contradict the neutral "still uploading" toast.
            if (stat.status !== 'rejected' || isTimeoutClassUploadError(stat.reason)) {
              file.status = 'done'
            } else {
              file.status = 'error'
              file.error = { message: toUploadErrorMessage(stat.reason) }
            }
            return file
          })
          .filter((file) => file.status === 'error')

        setFileList((fileList) => [...fileList.filter((f) => f.status === 'error')])
        setIsUploading(false)
        setIsUploadingForParent?.(false)
        setFilePasswords((filePasswords) => {
          return rejectedFiles.reduce(
            (acc, file) => ({
              ...acc,
              [file.uid]: filePasswords[file.uid],
            }),
            {},
          )
        })

        if (failedUploads.length > 0) {
          const genuineFailures = failedUploads.filter(
            (result) => !isTimeoutClassUploadError(result.reason),
          )
          const allTimedOut = genuineFailures.length === 0
          // Timed-out files may well have succeeded, so the error copy counts only genuine failures.
          const failFileWordPlural = pluralize(
            'File',
            allTimedOut ? failedUploads.length : genuineFailures.length,
          )
          toast({
            variant: allTimedOut ? 'default' : 'destructive',
            title: allTimedOut
              ? `Still uploading ${failFileWordPlural.toLocaleLowerCase()}`
              : `Error uploading ${failFileWordPlural}`,
            description: allTimedOut
              ? UPLOAD_STILL_PROCESSING_MESSAGE
              : `There was an error uploading ${genuineFailures.length} ${failFileWordPlural.toLocaleLowerCase()}. Please try again or contact support.`,
          })

          // Timed-out files plausibly succeeded server-side, so throwing would make
          // callers show a hard failure that contradicts the toast.
          if (!allTimedOut) {
            throw new Error('Failed to upload files')
          }
        }
      },
      [
        mutateAsync,
        company,
        categoryMetadata,
        toast,
        trackUpload,
        filePasswords,
        allowedFileTypes,
        setIsUploadingForParent,
      ],
    )

    // Handle batch upload for immediate upload
    useEffect(() => {
      if (!immediateUpload || !pendingFiles.length) return
      const filesToUpload = [...pendingFiles]
      // oxlint-disable-next-line react/set-state-in-effect
      setPendingFiles([]) // Clear pending files immediately

      // Start upload with minimum loading time
      setIsUploading(true)
      const uploadPromise = performUpload(filesToUpload)
      const minimumDelay = new Promise((resolve) => setTimeout(resolve, 1000))

      Promise.all([uploadPromise, minimumDelay]).finally(() => {
        setIsUploading(false)
      })
    }, [immediateUpload, pendingFiles, performUpload])

    useImperativeHandle(ref, () => ({
      isUploading,
      handleUpload: async (uploadCompanyId?: string) => {
        await performUpload(fileList, uploadCompanyId)
      },
    }))

    const handleBeforeUpload = async (file: FileWithStatus): Promise<boolean> => {
      if (file.size >= DEFAULT_UPLOAD_CONSTRAINTS.maxSize) {
        toast({
          variant: 'destructive',
          title: `File ${file.name} is over ${DEFAULT_UPLOAD_CONSTRAINTS.maxSize / 1024 / 1024}MB`,
        })
        return false
      }

      if (!isSupportedFileType(file, allowedFileTypes)) {
        file.error = {
          message: `Type of file (${file.type}) is not supported`,
        }
        file.status = 'error'
      }

      file.isEncrypted = await isFilePasswordProtected(file)

      if (immediateUpload) {
        // For immediate upload, collect files for batch upload
        setPendingFiles((prev) => [...prev, file])
      } else {
        // For manual upload, add to fileList
        setFileList((fileList) => [...fileList, file])
      }
      return false
    }

    const handleRemove = (file: UploadFileWithStatus) => {
      setFileList((fileList) => {
        const index = fileList.findIndex((f) => f.uid === file.uid)
        const newFileList = fileList.slice()
        newFileList.splice(index, 1)
        return newFileList
      })
      setFilePasswords((filePasswords) => {
        const { [file.uid]: _, ...newFilePasswords } = filePasswords
        return newFilePasswords
      })
    }

    return (
      <div>
        {!hideTitle && (
          <h3 className='mb-2 text-label-default'>
            {company
              ? `Upload ${categoryMetadata?.subcategory ? artifactSubcategoryLabel[categoryMetadata?.subcategory] : 'Files'} for ${company.name}`
              : 'Upload Files for Multiple Third-Parties (auto-detected)'}
          </h3>
        )}

        <Upload
          multiple
          fileList={fileList}
          beforeUpload={handleBeforeUpload}
          onRemove={handleRemove}
          filePasswords={filePasswords}
          onPasswordChange={setFilePasswords}
          hideList={immediateUpload}
          isUploading={isUploading}
          supportedFormatsHint={SUPPORTED_FILE_TYPES_HINT}
        />
      </div>
    )
  },
)

UploadFiles.displayName = 'UploadFiles'
